import React, { useContext, useEffect, useRef, useState } from "react";
import "./styles.css";
import PATHS from "../../Routes/paths";
import {
  AutoComplete,
  Breadcrumb,
  SuccessPopupModal,
} from "../../Components/Atoms";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { ALERT_VEHICLE_TABLE_HEADERS } from "../../Library/tableHeaders";
import { Button, useToast } from "@chakra-ui/react";
import { GEOFENCE_TRIGGER_TYPE, LOC_OPTIONS, OVERSPEED_TRIGGER_TYPE, SPEED_ALERTS_TITLE } from "../../Library/constants";
import { getAlerts, saveAlert, updateAlert } from "../../APIs";
import { isEqual } from "lodash";
import { AlertsVehiclesTable } from "../../Components/Organisms";
import OverspeedLocationSelector from "../../Components/Organisms/OverspeedLocationSelector";
import { StoreContext } from "../../Library/contexts";

const SetOverSpeedAlert = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  const formInitialState = {
    sl: "",
    stln: null,
    selectedVehicles: [],
  };
  const [formData, setFormData] = useState(formInitialState);
  const [isOpen, setIsOpen] = useState(false);
  const [errMsg, setErrMsg] = useState("");
  const SelectedVids = useRef([]);
  const [isEdit, setIsEdit] = useState(false);
  const [triggerRender, setTriggerRender] = useState(false);
  const [geofenceData, setGeofenceData] = useState(null);
  const [selectedRows, setSelectedRows] = useState([]);
  const initialState = useRef({});
  const Toast = useToast();
  const { allOverspeedAlerts, setAllOverspeedAlerts, setAllGeofences } = useContext(StoreContext);

  const setFormFieldData = (key) => (value) => {
    setFormData((prev) => ({ ...prev, [key]: value }));
    setErrMsg("");
  };

  const speedLimitOptions = [
    { value: 1, label: "For More Than 10 seconds", seconds: 10 },
    { value: 2, label: "For More Than 1 minutes", seconds: 60 },
    { value: 3, label: "For More Than 3 minutes", seconds: 60 * 3 },
    { value: 4, label: "For More Than 5 minutes", seconds: 60 * 5 },
  ];

  const breadcrumbPaths = [
    {
      idx: 1,
      name: "Overspeed Alerts",
      link: `${PATHS.SET_ALERTS}?tab=${2}`,
    },
    { idx: 2, name: isEdit ? "Edit Overspeed Alerts" : "Set Overspeed Alerts", isCurrentPage: true },
  ];

  const saveOverSpeedAlert = (data) => {
    saveAlert(data, (err) => {
      if (err) setErrMsg(err);
      else {
        setIsOpen(true);
        setAllGeofences(prev => prev.filter(fence => fence.idx !== -1));
      }
    });
  }

  const getGeofenceData = (fencedata, update, vidArr) => {
    let data = { ...fencedata };
    delete data.circle;
    delete data.polygon;
    delete data.insert;
    delete data.update;
    if (!update) delete data.idx;
    if (!update || (update && vidArr)) data.vehIdArr = SelectedVids.current;

    return data;
  }

  const submitHandler = () => {
    if (!formData["sl"]) return setErrMsg("Please enter overspeed limit");
    else if (!formData["stln"]) return setErrMsg("Please select time duration");
    else if (!SelectedVids.current || !SelectedVids.current.length) return setErrMsg("Please select vehicles for this trigger");

    let data = {
      title: SPEED_ALERTS_TITLE,
      spdLmt: parseInt(formData["sl"]),
      typ: OVERSPEED_TRIGGER_TYPE,
      ts: Date.now(),
      vehIdArr: SelectedVids.current,
    }

    let selectedSpdLmtDur = speedLimitOptions.find(lmt => lmt.value === formData["stln"]);
    if (selectedSpdLmtDur && selectedSpdLmtDur.seconds) data.thrshld = (selectedSpdLmtDur.seconds * 1000);
    else return setErrMsg("Please select valid time duration");

    if (geofenceData && geofenceData.selectedLocType) {
      if (geofenceData.selectedLocType !== LOC_OPTIONS.APPLY_EVERYWHERE && !geofenceData.idx) return setErrMsg("Please select existing geofence or create a new one");

      if (geofenceData.insert || geofenceData.idx === -1) {
        let dataToUpdate = getGeofenceData(geofenceData);
        delete dataToUpdate.selectedLocType;
        dataToUpdate.isEnabled = false;
        saveAlert(dataToUpdate, (error, resp) => {
          if (error) Toast({ isClosable: true, description: error || "Something went wrong", position: "bottom", variant: "subtle", duration: 3000 });
          else {
            data.gfId = resp;
            if (geofenceData.selectedLocType === LOC_OPTIONS.OUTSIDE) data.out = true;
            saveOverSpeedAlert(data);
          }
        });
      } else if (geofenceData.update) {
        let dataToUpdate = getGeofenceData(geofenceData, true, !!data.vehIdArr);
        delete dataToUpdate.selectedLocType;
        updateAlert(dataToUpdate, (error) => {
          if (error) Toast({ isClosable: true, description: `${error || "Something went wrong"}. You can update the geofence from geofences tab separately`, position: "bottom", variant: "subtle", duration: 3000 });
          if (initialState.current.gfId !== geofenceData.idx) data.gfId = geofenceData.idx;
          if (geofenceData.selectedLocType === LOC_OPTIONS.OUTSIDE) data.out = true;

          saveOverSpeedAlert(data);
        });
      } else {
        if (geofenceData.selectedLocType === LOC_OPTIONS.OUTSIDE) data.out = true;
        data.gfId = geofenceData.idx;
        saveOverSpeedAlert(data);
      }
    } else saveOverSpeedAlert(data);
  }

  const handleRowSelect = (_, selected) => {
    if (selected && selected.length) {
      SelectedVids.current = selected.map(selection => selection.original.idx);
      setSelectedRows(selected.map((_, index) => index));
    }
  }

  const updateAlertChanges = (data) => {
    updateAlert(data, (err) => {
      if (err) {
        setErrMsg("Error Saving Overspeed Alert");
        console.error("Error Saving Overspeed Alert", err);
      } else setIsOpen(true);
    });
  }

  const saveHandler = () => {
    if (!formData["sl"]) return setErrMsg("Please enter overspeed limit");
    else if (!formData["stln"]) return setErrMsg("Please select time duration");
    else if (!SelectedVids.current || !SelectedVids.current.length) return setErrMsg("Please select vehicles for this trigger");

    let idx = searchParams.get("idx");
    let data = { typ: OVERSPEED_TRIGGER_TYPE };

    if (idx && Number(idx)) data.idx = Number(idx);

    if(!isEqual(initialState.current?.vehIdArr?.sort(), SelectedVids.current?.sort())){
      data.vehIdArr = SelectedVids.current;
    }

    if(initialState.current.spdLmt !== parseInt(formData["sl"])){
      data.spdLmt = parseInt(formData["sl"]);
    }

    const speedLimitOption = speedLimitOptions.find(lmt => lmt.value === formData["stln"]);
    if(initialState.current.stln !== speedLimitOption.value){
      data.thrshld = speedLimitOption.seconds * 1000;
    }

    if (geofenceData) {
      if (geofenceData.selectedLocType === LOC_OPTIONS.OUTSIDE) {
        if (!initialState.current.out) data.out = true;
        if (initialState.current.gfId !== geofenceData.idx) data.gfId = geofenceData.idx;
      }
      else if (geofenceData.selectedLocType === LOC_OPTIONS.INSIDE) {
        if (initialState.current.out) data.out = false;
        if (initialState.current.gfId !== geofenceData.idx) data.gfId = geofenceData.idx;
      }
      else if (geofenceData.selectedLocType === LOC_OPTIONS.APPLY_EVERYWHERE) {
        data.gfId = null;
        if (initialState.current.out) data.out = false;
      }
    }

    let keysToUpdate = Object.keys(data).length;

    if (geofenceData?.insert || geofenceData?.idx === -1) {
      let dataToUpdate = getGeofenceData(geofenceData);
      delete dataToUpdate.selectedLocType;
      dataToUpdate.isEnabled = false;
      saveAlert(dataToUpdate, (error, resp) => {
        if (error) Toast({ isClosable: true, description: error || "Something went wrong", position: "bottom", variant: "subtle", duration: 3000 });
        else {
          data.gfId = resp;
          if (geofenceData.selectedLocType === LOC_OPTIONS.OUTSIDE) data.out = true;
          updateAlertChanges(data);
        }
      });
    } else if (geofenceData?.update) {
      let dataToUpdate = getGeofenceData(geofenceData, true, !!data.vehIdArr);
      delete dataToUpdate.selectedLocType;
      updateAlert(dataToUpdate, (error) => {
        if (error) Toast({ isClosable: true, description: `${error || "Something went wrong"}. You can update the geofence from geofences tab separately`, position: "bottom", variant: "subtle", duration: 3000 });
        if (initialState.current.gfId !== geofenceData.idx) data.gfId = geofenceData.idx;
        if (geofenceData.selectedLocType === LOC_OPTIONS.OUTSIDE) data.out = true;

        if (keysToUpdate <= 2) setIsOpen(true);
        else updateAlertChanges(data);
      });
    } else {
      if(keysToUpdate <= 2) return setErrMsg("No Changes Made to Save !!");
      updateAlertChanges(data);
    }
  }

  useEffect(() => {
    const edit = searchParams.get("edit");
    let idx = searchParams.get("idx");

    if (edit && idx && allOverspeedAlerts.length) {
      setIsEdit(true);
      let alertData = location.state;
      let alertToEdit = allOverspeedAlerts.find(alert => alert.idx === +idx);
      if (alertToEdit) alertData = alertToEdit;

      setFormData({
        sl: alertData.spdLmt,
        stln: speedLimitOptions.find(lmt => ((lmt.seconds === (alertData.threshold / 1000)) || (lmt.seconds === (alertData.thrshld / 1000))))?.value || null,
        selectedVehicles: alertData.vehIdArr,
      });

      SelectedVids.current = alertData.vehIdArr;

      initialState.current = {
        spdLmt: alertData.spdLmt,
        stln: speedLimitOptions.find(lmt => lmt.seconds === alertData.threshold / 1000)?.value || null,
        vehIdArr: alertData.vehIdArr,
        gfId: alertData.gfId,
        out: alertData.out,
      };

      setTriggerRender((prev) => !prev);
    }

  }, [ allOverspeedAlerts ]);

  return (
    <div className="set-os-alert">
      <div className="set-os-alert-breadcrumb">
        <Breadcrumb paths={breadcrumbPaths} />
      </div>
      <div className="set-os-alert-form">
        <form className="set-os-form">
          <div className="set-os-form-field-cnt">
            <div className="set-os-title-container">
              <span>Set Overspeed Alert</span>
              <hr />
            </div>
            <div className="set-os-input-fields">
              <label className="kms-input-label">
                <p>When speed exceeds</p>
                <input placeholder="0" type="number" value={formData["sl"]} onChange={(event) => setFormFieldData("sl")(event.target.value)} />
                <p>km/hr</p>
              </label>
              <AutoComplete
                autoCompleteClassName={"add-client-autocomplete"}
                className={`add-client-autocomplete-field`}
                labelClassName={"add-client-autocomplete-field-label"}
                options={speedLimitOptions}
                inputName={"timeDuration"}
                placeholder={"Select Time Duration"}
                inputCover={"set-os-custom-cover-w"}
                value={formData["stln"]}
                isRequired={true}
                onChange={(_, durationobj) => {
                  setFormFieldData("stln")(durationobj.value);
                }}
              />
            </div>
          </div>

          <OverspeedLocationSelector handleLocationData={setGeofenceData} />

          <AlertsVehiclesTable key={triggerRender} headers={ALERT_VEHICLE_TABLE_HEADERS} className={"set-os-alert-table"} handleRowSelect={handleRowSelect} selectedRows={SelectedVids.current} />

          <div className="set-os-alert-submit-button-container">
            <p className="error-message os-alert-error-message">{errMsg}</p>
            <Button className="set-os-alert-button" onClick={isEdit ? saveHandler : submitHandler}>
              { isEdit ? "Save Overspeed Alerts" : "Set Overspeed Alerts"}
            </Button>
          </div>
        </form>
      </div>
      <SuccessPopupModal
        open={isOpen}
        modalMsg={"Overspeed Alert Set successfully"}
        closeBtn={"Okay"}
        modalSize={"lg"}
        onModalClose={() =>
          navigate(`${PATHS.SET_ALERTS}?tab=2`)
        }
      />
    </div>
  );
};

export default SetOverSpeedAlert;
